<template>
  <v-bottom-navigation
    role="navigation"
    :aria-label="$t('components.menu.landmarkLabel')"
    :height="isLoggingOut ? '70' : '60'"
    app
    color="primary"
  >
    <v-flex column>
      <v-progress-linear
        v-show="isLoggingOut"
        style="margin:0"
        color="accent"
        indeterminate
        aria-live="polite"
        aria-label="Logging out"
      />
      <v-sheet elevation="2">
        <v-tabs
          show-arrows
          icons-and-text
          centered
          fixed-tabs
          next-icon="fad fa-chevron-right"
          prev-icon="fad fa-chevron-left"
          slider-color="accent"
          color="accent"
          class="main-menu"
          role="tablist"
          aria-label="Main Menu"
        >
          <!-- Logout ----------------------------------------------------- -->
          <v-tab
            v-show="isLogged"
            :key="'logout'"
            role="tab"
            text
            :aria-selected="false"
            @click="onLogout()"
          >
            {{ $t('components.menu.logout') }}
            <v-icon
              small
              aria-hidden="true"
            >
              fal fa-sign-out
            </v-icon>
          </v-tab>

          <v-menu
            v-if="downloadGames.length && $vuetify.breakpoint.mdAndUp"
            v-model="downloadMenuOpen"
            top
            open-on-hover
          >
            <template #activator="{ on, attrs }">
              <v-btn
                aria-label="Download"
                text
                v-bind="attrs"
                class="font-weight-medium text-body-2 text-uppercase"
                :aria-haspopup="true"
                :aria-expanded="downloadMenuOpen"
                v-on="on"
              >
                {{ $t("components.menu.downloads") }}
                <v-icon
                  small
                  aria-hidden="true"
                  class="mb-2"
                >
                  fal fa-cloud-download
                </v-icon>
              </v-btn>
            </template>
            <v-list role="menu">
              <v-list-item
                v-for="game in downloadGames"
                :key="`dl-${game.id}`"
                role="menuitem"
                tabindex="0"
                link
                @click="goTo(game.link)"
                @keydown.enter="goTo(game.link)"
                @keydown.space.prevent="goTo(game.link)"
              >
                <div
                  v-if="game.useImageAsBackground"
                  class="v-list-item-background-image"
                  :style="`background-image: url('${game.img}')`"
                >
                  <div class="v-list-item-download-labels">
                    <span
                      v-if="game.isNew"
                      class="downloadLabel downloadNew"
                    >New</span>
                    <span
                      v-if="game.isRecommend"
                      class="downloadLabel downloadRecommend"
                    >Recommend</span>
                    <span
                      v-if="game.isRecommend"
                      class="downloadLabel downloadComingSoon"
                    >Coming Soon</span>
                  </div>
                </div>

                <template v-else>
                  <v-list-item-avatar size="25">
                    <v-icon>{{ game.icon }}</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-title
                    class="text-no-wrap"
                  >
                    {{ $t(game.title) }}
                    <div class="v-list-item-download-labels">
                      <span
                        v-if="game.isNew"
                        class="downloadLabel downloadNew"
                      >New</span>
                      <span
                        v-if="game.isRecommend"
                        class="downloadLabel downloadRecommend"
                      >Recommend</span>
                      <span
                        v-if="game.isRecommend"
                        class="downloadLabel downloadComingSoon"
                      >Coming Soon</span>
                    </div>
                  </v-list-item-title>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>

          <system-requirements />

          <v-tab
            v-for="link in links"
            :key="link.id"
            role="tab"
            flat
            :aria-selected="false"
            @click="goToDynamic(link)"
          >
            {{ $t(link.text) }}
            <v-icon
              small
              aria-hidden="true"
            >
              {{ link.icon }}
            </v-icon>
          </v-tab>

          <v-menu
            v-if="chapters.length"
            v-model="chaptersMenuOpen"
            top
            open-on-hover
          >
            <template #activator="{ on, attrs }">
              <v-btn
                aria-label="Chapters"
                v-bind="attrs"
                text
                class="main-menu-button font-weight-medium text-body-2"
                :aria-haspopup="true"
                :aria-expanded="chaptersMenuOpen"
                v-on="on"
              >
                {{ $t("components.menu.chapters") }}
                <v-icon
                  small
                  class="mb-2"
                  aria-hidden="true"
                >
                  fal fa-layer-plus
                </v-icon>
              </v-btn>
            </template>

            <v-list role="menu">
              <v-list-item
                v-for="chapter in chapters"
                :key="`ch-${chapter.id}`"
                role="menuitem"
                tabindex="0"
                @click="goTo(chapter.link)"
                @keydown.enter="goTo(chapter.link)"
                @keydown.space.prevent="goTo(chapter.link)"
              >
                <v-list-item-avatar size="25">
                  <img
                    :src="chapter.img"
                    :alt="$t(chapter.title)"
                  >
                </v-list-item-avatar>
                <v-list-item-title
                  v-text="$t(chapter.title)"
                />
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu
            v-if="langs.length"
            v-model="languagesMenuOpen"
            flat
            top
            open-on-hover
          >
            <template #activator="{ on, attrs }">
              <v-btn
                aria-label="Languages"
                text
                v-bind="attrs"
                class="main-menu-button font-weight-medium text-body-2"
                :aria-haspopup="true"
                :aria-expanded="languagesMenuOpen"
                v-on="on"
              >
                {{ $t("components.menu.languages") }}
                <v-icon
                  small
                  class="mb-2"
                  aria-hidden="true"
                >
                  fal fa-language
                </v-icon>
              </v-btn>
            </template>
            <v-list role="menu">
              <v-list-item
                v-for="lang in langs"
                :key="lang.id"
                role="menuitem"
                tabindex="0"
                :class="
                  lang.code == currentLang
                    ? 'accent--text'
                    : 'whitesmoke--text'
                "
                @click="onLangUpdate(lang.code)"
                @keydown.enter="onLangUpdate(lang.code)"
                @keydown.space.prevent="onLangUpdate(lang.code)"
              >
                <v-list-item-avatar size="25">
                  <img
                    :src="lang.img"
                    :alt="lang.name"
                  >
                </v-list-item-avatar>
                <v-list-item-title
                  v-text="lang.name"
                />
              </v-list-item>
            </v-list>
          </v-menu>

          <v-tab
            v-if="isCompletedOAuth"
            role="tab"
            :aria-selected="false"
            :to="toAbout()"
          >
            {{ $t("components.menu.about") }}
            <v-icon
              small
              color="grey darken-1"
              aria-hidden="true"
            >
              fal fa-info
            </v-icon>
          </v-tab>

          <v-tab
            v-if="isCompletedOAuth"
            role="tab"
            :aria-selected="false"
            :to="toFAQ()"
          >
            {{ $t("components.menu.faq") }}
            <v-icon
              small
              aria-hidden="true"
              color="grey darken-1"
            >
              fad fa-question
            </v-icon>
          </v-tab>

          <v-tab
            v-if="isCompletedOAuth"
            role="tab"
            :aria-selected="false"
            :to="toTc()"
          >
            {{ $t("components.menu.tc") }}
            <v-icon
              small
              aria-hidden="true"
              color="grey darken-1"
            >
              fal fa-balance-scale
            </v-icon>
          </v-tab>
        </v-tabs>
      </v-sheet>
    </v-flex>
  </v-bottom-navigation>
</template>

<script>
import sidebarItems from "@/constants/sidebar.items";
import langs from "@/constants/langs.list";
import downloadGames from "@/constants/download.games";
import chapters from "@/constants/chapters.list";
import links from "@/constants/links.js";
import { getLang } from "@/utils/global.utils";
import actions from "@/store/actions";
import { mapGetters } from "vuex";

export default {
    name: "MenuComp",
    components: {
        SystemRequirements: () =>
            import("@/components/Views/SystemRequirements.vue")
    },
    data: () => ({
        downloadGames: downloadGames.list,
        langs: langs.list,
        chapters: chapters.list,
        isLoggingOut: false,
        isMiniVagriant: true,
        downloadMenuOpen: false,
        chaptersMenuOpen: false,
        languagesMenuOpen: false
    }),
    computed: {
        ...mapGetters(['isLogged', 'username', 'isAdmin', 'isDeveloper', 'isModerator', 'getMenuVisibility']),

        links() {
            return this.isLogged
                ? this.username
                    ? this.isAdmin ||
                      this.isDeveloper ||
                      this.isModerator
                        ? [
                              ...sidebarItems.mainMenu.loggedAdmin,
                              ...sidebarItems.mainMenu.loggedCompleted
                          ]
                        : sidebarItems.mainMenu.loggedCompleted
                    : sidebarItems.mainMenu.logged
                : sidebarItems.mainMenu.guest;
        },
        isCompletedOAuth() {
            return (
                (this.isLogged &&
                    this.username) ||
                !this.isLogged
            );
        },
        currentLang: () => getLang(),
        drawer: {
            get() {
                return this.getMenuVisibility;
            },
            set: val => {
                actions.updateComponentVisibility({ menu: val });
            }
        },
        ...mapGetters(["isLogged"])
    },
    watch: {
        isLogged(newValue) {
            if (!newValue) {
                this.isLoggingOut = false;
            }
        }
    },
    methods: {
        goTo(link) {
            // track event
            // this.$ga.event("menu", "link", link);
            // go to link
            window.open(link);
        },
        goToDynamic(link) {
            if (link.redirect) {
                this.goTo(link.to)

                return;
            }

            // track event
            // this.$ga.event("menu", "link", link);

            this.$router.push(link.to)
        },
        onLogout() {
            if (this.isLogged) {
                // logout
                this.isLoggingOut = true;
                this.$api.logout();
            }
        },
        toTc() {
            return links.routes.tcConditions;
        },
        toAbout() {
            return links.routes.about;
        },
        toFAQ() {
            return links.routes.faq;
        },
        setMiniVariantVisiblity() {
            // this.$ga.event(
            //     "header-menu",
            //     "visibility",
            //     String(!this.isMiniVagriant)
            // );
            this.isMiniVagriant = !this.isMiniVagriant;
        },
        onLangUpdate(code) {
            this.$lang.setLocale(code);

            this.$api.updateLanguage(code)
        }
    }
};
</script>

<style lang="scss">
    .main-menu {
        &-button {
            width: 100%;
        }
    }
</style>
